import { Box, Stack, Text } from '@inflearn/ds-react-next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SelectionSparkle } from './SelectionSparkle';
import { CDN_BASE_PATH } from './itemData';
import type { DressItem } from './types';

interface SlotMachineProps {
  items: DressItem[];
  onItemSelected: (item: DressItem) => void;
  stepIndex: number; // 단계별 속도 조절용
}

export const SlotMachine = ({ items, onItemSelected, stepIndex }: SlotMachineProps) => {
  const [arrowIndex, setArrowIndex] = useState(0);
  const [direction, setDirection] = useState<'right' | 'left'>('right');
  const [isAnimating, setIsAnimating] = useState(true);
  const [selectedItem, setSelectedItem] = useState<DressItem | null>(null);
  const [finalArrowPosition, setFinalArrowPosition] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 단계별로 다른 속도 설정 (ms 단위, 낮을수록 빠름)
  // 마지막 단계(얼굴)가 가장 빠르도록 설정
  const speeds = [100, 80, 60, 40, 25]; // 5단계 - 마지막(얼굴)이 가장 빠름!
  const currentSpeed = speeds[stepIndex] || 60;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAnimating) {
      return;
    }

    intervalRef.current = setInterval(() => {
      setArrowIndex((prev) => {
        const next = direction === 'right' ? prev + 1 : prev - 1;

        // 경계 처리
        if (next >= items.length) {
          setDirection('left');
          return items.length - 1;
        }
        if (next < 0) {
          setDirection('right');
          return 0;
        }

        return next;
      });
    }, currentSpeed);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAnimating, direction, items.length, currentSpeed]);

  const handleStop = useCallback(() => {
    if (!isAnimating) {
      return;
    }

    setIsAnimating(false);
    setFinalArrowPosition(arrowIndex); // 화살표 위치 고정
    const selected = items[arrowIndex];
    setSelectedItem(selected);

    // 약간의 딜레이 후 선택 완료 (화살표 위치 유지)
    timeoutRef.current = setTimeout(() => {
      onItemSelected(selected);
    }, 800);
  }, [isAnimating, items, arrowIndex, onItemSelected]);

  // 컴포넌트 언마운트 시 timeout 정리
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 스페이스바 입력 처리
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && isAnimating) {
        e.preventDefault();
        handleStop();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAnimating, handleStop]);

  return (
    <Stack gap={24} align="center">
      <Box pos="absolute" w="100%" h="372px" bottom={0}>
        <Box pos="absolute" top={0} left={0} w="100%" h="372">
          <img
            src={`${CDN_BASE_PATH}/ch_slot_bg.png?w=1760`}
            style={{
              width: '100%',
              height: '372px',
              zIndex: 8,
            }}
          />
        </Box>
        <Box pos="absolute" top={0} left={0} w="100%" h="372px" css={{ zIndex: 11 }}>
          <img
            src={`${CDN_BASE_PATH}/ch_slot_frame.png?w=1760`}
            style={{
              width: '100%',
              height: '372px',
            }}
          />
        </Box>
        <Box
          style={{
            position: 'absolute',
            bottom: '0px',
            left: `calc(110px + ${
              (finalArrowPosition !== null ? finalArrowPosition : arrowIndex) * (124 + 16)
            }px + 62px)`, // 124px(시작위치) + 아이템 이동 + 62px(124px 아이템의 정확한 중앙)
            transition: isAnimating ? `left ${currentSpeed}ms linear` : 'none',
            transform: isAnimating ? 'scale(1) translateX(-50%)' : 'scale(1) translateX(-50%)',
            transformOrigin: 'center top',
            zIndex: 12,
          }}
        >
          <img
            src={`${CDN_BASE_PATH}/ch_arrow.png?w=200`}
            alt="arrow"
            style={{
              width: '100px',
              height: '100px',
              display: 'block',
            }}
          />
        </Box>

        {/* 슬롯 아이템들 */}
        <Box
          pos="absolute"
          left={110}
          bottom={65}
          style={{
            display: 'flex',
            gap: '16px',
          }}
        >
          {items.map((item) => {
            const isSelected = selectedItem?.id === item.id;

            return (
              <Box
                key={item.id}
                pos="relative"
                style={{
                  width: '124px',
                  height: '124px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  borderRadius: '12px',
                  border: isSelected
                    ? '8px solid rgba(226, 103, 2, 1)'
                    : '8px solid rgba(226, 103, 2, 0.75)',
                  backgroundColor: isSelected ? '#FFE8CC' : '#FFF9E6',
                  boxShadow: isSelected
                    ? '0 0 10px 0 rgba(255, 234, 203, 0.1)'
                    : '0 0 10px 0 rgba(229, 95, 95, 0.05)',
                  transition: 'all 0.2s ease',
                }}
              >
                {isSelected && (
                  <>
                    <Box
                      pos="absolute"
                      top={0}
                      left={0}
                      css={{
                        width: '124px',
                        height: '124px',
                        zIndex: 8,
                      }}
                    >
                      <Box
                        css={{
                          borderRadius: '999px',
                          background:
                            'linear-gradient(135deg, rgba(254, 243, 199, 0.8) 0%, rgba(253, 230, 138, 0.8) 100%)',
                        }}
                      />
                    </Box>
                    <SelectionSparkle isActive={isSelected} />
                  </>
                )}
                {item.emoji.startsWith(CDN_BASE_PATH) ? (
                  <img
                    src={item.emoji}
                    alt={item.name}
                    style={{
                      width: 'auto',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: '4px',
                    }}
                  />
                ) : (
                  <Text
                    style={{
                      fontSize: '48px',
                      lineHeight: 1,
                    }}
                  >
                    {item.emoji}
                  </Text>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Stack>
  );
};
